import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyBulkLocationEdit,
  fetchBulkLocationEditExportCsv,
  validateBulkLocationEdit,
} from '../location/requests/location-requests';
import { getErrorMessage } from '../../util/NetworkErrorUtil';
import { ASSISTANT_NAME } from './assistantIdentity';
import type { BulkLocationEditApplyResult, BulkLocationEditPreview } from '../../types/BulkLocationEdit';
import {
  BulkEditFormatError,
  buildBulkEditCsv,
  cellAt,
  csvToFile,
  parseBulkEditExport,
} from './bulkEditCsv';
import type { ParsedBulkEditExport, RowUpdate } from './bulkEditCsv';
import { applyStrategy, assessRow, detectBulletMarker, optionsForConcern } from './descriptionAssessment';
import { LOCATION_DESCRIPTION_COLUMN } from './editableFields';
import { parseIntent } from './intentParser';
import { resolveLocationFromMessage } from './locationResolver';
import type { LocationCandidate } from './locationResolver';
import { describeScope, detectScope, stripScopePhrase } from './locationScope';
import type { LocationScope } from './locationScope';
import { resolvePeriod } from './periodParser';
import { buildExamples } from './exampleCopy';
import { buildCapabilityReply, isHelpRequest } from './helpRequest';
import type { AssistantCapabilities } from './assistantCapabilities';
import {
  claimantFor,
  describeAnswerableQuestions,
  isQuestion,
  queriesFor,
  queryById,
  toolManifest,
} from './knowledgeRegistry';
import type { KnowledgeAnswer } from './knowledgeRegistry';
import { createPlanner, type AssistantPlanner, type QuestionPlan } from './planner';
import { createAgent, type AssistantAgent } from './agent';
import { noGeocode, type GeocodeFn } from './geocode';
import { matchNavigation } from './navigationGuide';
import type { LocationStatus } from '../../types/LocationStatus';
import {
  clearConversation as clearStoredConversation,
  highestMessageNumber,
  loadConversation,
  restoreMessages,
  saveConversation,
} from './assistantStorage';
import type {
  AssessedRow,
  AssistantIntent,
  AssistantMessage,
  AssistantPhase,
  ConcernType,
  MessageAttachment,
  ResolutionStrategy,
} from './types';

/** The minimum the assistant needs from the page's location list. */
export interface AssistantLocation {
  id: string;
  status: LocationStatus;
  /**
   * Used only to build example copy around a location the account actually
   * owns. Optional because status scoping — the reason this list exists — does
   * not need it.
   */
  locationName?: string;
}

/**
 * Builds the message attachment for a knowledge answer. An answer may carry a
 * breakdown, a call-to-action link, both, or neither; the attachment exists only
 * when there is something to render, and the link rides on the breakdown kind so
 * a single message shows the matches and the link to explore them together.
 */
const answerAttachment = (answer: KnowledgeAnswer): MessageAttachment | undefined => {
  if (!answer.breakdown?.length && !answer.link) return undefined;
  return { kind: 'breakdown', rows: answer.breakdown ?? [], link: answer.link };
};

interface UseAssistantConversationOptions {
  /** Scopes stored conversation state so it cannot leak between accounts. */
  userId: string;
  /**
   * The page's locations, used only to resolve status-filtered scopes such as
   * "all my active locations". The bulk-edit export carries no status column,
   * so without this the assistant cannot honour those requests.
   */
  locations?: AssistantLocation[];
  /** Called after a successful apply so the caller can refresh its list. */
  onApplied?: () => void;
  /**
   * Moves the app to a route, used when the user confirms a navigation offer.
   * Injected rather than taken from `useNavigate` directly so the conversation
   * logic stays testable without a router; the panel that mounts under the app
   * router supplies the real navigator. Defaults to a no-op.
   */
  onNavigate?: (path: string) => void;
  /**
   * What the assistant may do for this account. Defaults to a fully-capable
   * supplier so existing supplier-only callers keep working unchanged.
   */
  capabilities?: AssistantCapabilities;
  /**
   * Selects which skill answers a question. Defaults to the deterministic
   * phrase matcher, so the assistant behaves exactly as it did before the
   * planner seam existed unless a model-backed planner is passed in.
   */
  planner?: AssistantPlanner;
  /**
   * Backend agent consulted only when no local skill answers a question.
   * Defaults to a disabled agent, so the assistant declines exactly as before
   * unless a caller supplies an enabled one.
   */
  agent?: AssistantAgent;
  /**
   * Resolves a place description (a city, a ZIP) to coordinates, for the
   * marketplace location search. Defaults to a geocoder that always declines, so
   * that skill degrades to asking for a clearer place rather than crashing when
   * no map is loaded (as in tests). The provider supplies the real one from
   * `GeocoderContext`.
   */
  geocode?: GeocodeFn;
}

const DEFAULT_CAPABILITIES: AssistantCapabilities = {
  audience: 'supplier',
  isStaff: false,
  isImpersonating: false,
  canEditLocations: true,
};

/** Phrase matching only, until a caller supplies a model-backed planner. */
const DEFAULT_PLANNER = createPlanner();

/** Disabled by default: the fallback agent never reaches the network unless enabled. */
const DEFAULT_AGENT = createAgent();

/**
 * Shown when the assistant has no skill that applies to this account type.
 *
 * Says so plainly rather than listing supplier abilities the user cannot use —
 * offering an action that will be refused is worse than admitting the gap.
 */
const NO_SKILLS_YET =
  `I do not have any skills for this account yet. ${ASSISTANT_NAME} is new, and more are ` +
  'being added — for now I cannot help with this one.';

/** Shown when a change is understood but this account may not make it. */
const CANNOT_EDIT =
  'I can only make changes on a supplier account with a confirmed email address, so I have ' +
  'not changed anything.';

/**
 * The decline for a question no skill covers.
 *
 * Deliberately short. This used to recite every answerable question, which grew
 * with the registry until a one-line "I don't know" arrived as a paragraph of
 * things the user had not asked about — and read as broken rather than humble.
 * The list still exists, one question away, for anyone who wants it.
 */
const CANNOT_ANSWER_YET =
  'I cannot answer that one yet \u2014 I am still learning. Ask me "what can you do" to ' +
  'see the full list.';

const pluralize = (count: number, singular: string): string =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

/**
 * Order in which grouped concerns are put to the user. Skippable concerns come
 * first so the set still being changed shrinks before the formatting questions.
 */
const CONCERN_ORDER: ConcernType[] = ['ALREADY_PRESENT', 'EMPTY_SOURCE', 'LIST_FORMAT'];

/** Joins location names for prose, keeping long lists readable. */
const listNames = (names: string[], limit = 3): string => {
  if (names.length <= limit) {
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, limit).join(', ')} and ${names.length - limit} more`;
};

const greeting = (): AssistantMessage => ({
  id: 'greeting',
  role: 'assistant',
  // Kept short deliberately: the panel shows starter prompts alongside this
  // while the transcript is empty, so listing examples here would say it twice.
  text: `Hi, I am ${ASSISTANT_NAME}. Give me a task or ask me a question.`,
});

/** Rebuilds the opening state, restoring a stored conversation when there is one. */
const buildInitialState = (userId: string): { messages: AssistantMessage[]; input: string } => {
  const stored = loadConversation(userId);
  if (!stored || stored.messages.length === 0) {
    return { messages: [greeting()], input: stored?.input ?? '' };
  }

  const { messages, hadPendingAction } = restoreMessages(stored.messages);
  if (!hadPendingAction) return { messages, input: stored.input };

  return {
    messages: [
      ...messages,
      {
        id: 'restored',
        role: 'assistant',
        text:
          'I reopened this chat, so the change I had ready is no longer pending — ' +
          'nothing was saved. Ask me again if you still want it.',
      },
    ],
    input: stored.input,
  };
};

/** Delay before persisting, so typing does not serialise on every keystroke. */
const PERSIST_DEBOUNCE_MS = 300;

/**
 * Drives the assistant conversation across the user's editable locations.
 *
 * The flow is deliberately staged: parse the instruction, work out which
 * location it targets, draft or assess anything that needs judgement, then hand
 * a rebuilt CSV to the existing server-side dry run so the user confirms a real,
 * server-computed diff. Nothing is mutated until {@link confirmApply}.
 */
export const useAssistantConversation = ({
  userId,
  locations,
  onApplied,
  onNavigate,
  capabilities = DEFAULT_CAPABILITIES,
  planner = DEFAULT_PLANNER,
  agent = DEFAULT_AGENT,
  geocode = noGeocode,
}: UseAssistantConversationOptions) => {
  const [initialState] = useState(() => buildInitialState(userId));
  const [messages, setMessages] = useState<AssistantMessage[]>(initialState.messages);
  const [input, setInput] = useState(initialState.input);
  const [phase, setPhase] = useState<AssistantPhase>('idle');

  // Example copy is built from the account's own locations so it never invites
  // the user to type a location name they do not own, and from what this account
  // can actually do so no starter chip comes back refused.
  const examples = useMemo(
    () =>
      buildExamples(locations, {
        questionExamples: queriesFor(capabilities.audience).map((query) => query.example),
        canEditLocations: capabilities.canEditLocations,
      }),
    [locations, capabilities.audience, capabilities.canEditLocations],
  );

  // Working state for the in-flight request. Held in refs because it only
  // affects message content at transition points, never mid-render.
  const exportRef = useRef<ParsedBulkEditExport | null>(null);
  // One entry for a named location, many for a scoped request. Keeping a single
  // collection means there is one execution path rather than two.
  const targetsRef = useRef<LocationCandidate[]>([]);
  const scopeRef = useRef<LocationScope>({ kind: 'NAMED' });
  const pendingIntentRef = useRef<AssistantIntent | null>(null);
  const pendingRef = useRef<Map<string, RowUpdate>>(new Map());
  const assessedRef = useRef<AssessedRow[]>([]);
  // Concern types still to put to the user, asked once per type rather than
  // once per location.
  const concernQueueRef = useRef<ConcernType[]>([]);
  const appendTextRef = useRef('');
  const fileRef = useRef<File | null>(null);
  // Seeded from restored messages so reissued ids cannot collide as React keys.
  const idRef = useRef(highestMessageNumber(initialState.messages));

  useEffect(() => {
    const timer = setTimeout(
      () => saveConversation(userId, { messages, input }),
      PERSIST_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [userId, messages, input]);

  // The panel unmounts when the drawer closes, which would cancel a pending
  // debounce and lose the last keystrokes — the exact case persistence exists
  // for — so the latest state is flushed synchronously on unmount.
  const latestRef = useRef({ messages, input });
  latestRef.current = { messages, input };
  useEffect(() => () => saveConversation(userId, latestRef.current), [userId]);

  const nextId = () => {
    idRef.current += 1;
    return `m${idRef.current}`;
  };

  const push = useCallback(
    (role: 'user' | 'assistant', text: string, attachment?: MessageAttachment) => {
      setMessages((prev) => [...prev, { id: nextId(), role, text, attachment }]);
    },
    [],
  );

  /** Hides the controls on the most recent interactive message. */
  const resolveLastAttachment = useCallback(() => {
    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].attachment && !next[i].resolved) {
          next[i] = { ...next[i], resolved: true };
          break;
        }
      }
      return next;
    });
  }, []);

  const resetWorkingState = () => {
    exportRef.current = null;
    targetsRef.current = [];
    scopeRef.current = { kind: 'NAMED' };
    pendingIntentRef.current = null;
    pendingRef.current = new Map();
    assessedRef.current = [];
    concernQueueRef.current = [];
    appendTextRef.current = '';
    fileRef.current = null;
  };

  /** Fetches and parses the export, caching it for the current instruction. */
  const loadExport = useCallback(async (): Promise<ParsedBulkEditExport> => {
    if (exportRef.current) return exportRef.current;
    const source = parseBulkEditExport(await fetchBulkLocationEditExportCsv());
    exportRef.current = source;
    return source;
  }, []);

  /** The editable locations, read from the export so it matches what the server allows. */
  const candidatesFrom = (source: ParsedBulkEditExport): LocationCandidate[] =>
    source.rows.map((row) => ({
      id: cellAt(row, source.columns.locationId),
      name: cellAt(row, source.columns.locationName),
    }));

  const rowFor = (source: ParsedBulkEditExport, locationId: string): string[] => {
    const row = source.rows.find(
      (candidate) => cellAt(candidate, source.columns.locationId) === locationId,
    );
    if (!row) {
      throw new BulkEditFormatError(
        'I could not find that location in your editable locations, so I have not changed anything.',
      );
    }
    return row;
  };

  /**
   * Narrows the editable locations to those a scope selects.
   *
   * Status comes from the page's list rather than the export, so a location the
   * page does not know about is left out of a status-filtered scope — guessing
   * its status could either skip a location the user meant or edit one they
   * did not.
   */
  const targetsForScope = useCallback(
    (scope: LocationScope, candidates: LocationCandidate[]): LocationCandidate[] => {
      if (scope.kind !== 'STATUS') return candidates;

      const statusById = new Map((locations ?? []).map((entry) => [entry.id, entry.status]));
      return candidates.filter((candidate) => statusById.get(candidate.id) === scope.status);
    },
    [locations],
  );

  /** Builds the CSV from staged values and runs the server-side dry run. */
  const runValidation = useCallback(async () => {
    const source = exportRef.current;
    const pending = pendingRef.current;

    if (!source || pending.size === 0) {
      push('assistant', 'Nothing left to change, so I have not saved anything.');
      setPhase('idle');
      resetWorkingState();
      return;
    }

    setPhase('validating');

    try {
      const file = csvToFile(buildBulkEditCsv(source, pending));
      fileRef.current = file;

      const response = await validateBulkLocationEdit(file);
      const preview = response.body as BulkLocationEditPreview;

      if (preview.fileErrors.length > 0 || !preview.applyable) {
        push('assistant', 'The server rejected that change, so nothing was saved:', {
          kind: 'preview',
          preview,
        });
        setPhase('idle');
        resetWorkingState();
        return;
      }

      if (preview.fieldsChanged === 0) {
        push('assistant', 'That already matches what is saved, so there is nothing to change.');
        setPhase('idle');
        resetWorkingState();
        return;
      }

      // Naming the single location is clearer than a count; past that the count
      // is what conveys the blast radius, and the diff below names every one.
      // The server's own count is used so the wording cannot overstate the
      // reach when some targeted locations already match the requested value.
      const targeted = targetsRef.current.filter((target) => pending.has(target.id));
      const changedCount = preview.locationsWithChanges || targeted.length;
      const scopeText =
        changedCount === 1 && targeted.length === 1
          ? `on **${targeted[0].name}**`
          : `across **${pluralize(changedCount, 'location')}**`;

      push('assistant', `Here is exactly what will change ${scopeText}. Nothing is saved yet.`, {
        kind: 'preview',
        preview,
      });
      push(
        'assistant',
        changedCount === 1
          ? 'Save this?'
          : `Save these changes to ${pluralize(changedCount, 'location')}?`,
        { kind: 'confirm', fieldsChanged: preview.fieldsChanged },
      );
      setPhase('previewing');
    } catch (err) {
      const message =
        err instanceof BulkEditFormatError
          ? err.message
          : getErrorMessage(err) || 'I could not check that change. Nothing was saved.';
      push('assistant', message);
      setPhase('idle');
      resetWorkingState();
    }
  }, [push]);

  /** Stages every non-skipped assessment and runs the dry run. */
  const stageAssessedDescriptions = useCallback(() => {
    const pending = new Map<string, RowUpdate>();

    assessedRef.current.forEach((assessed) => {
      if (assessed.strategy === 'SKIP') return;
      pending.set(assessed.locationId, {
        [LOCATION_DESCRIPTION_COLUMN]: assessed.proposedDescription,
      });
    });

    if (pending.size === 0) {
      push('assistant', 'That leaves nothing to change, so nothing was saved.');
      setPhase('idle');
      resetWorkingState();
      return;
    }

    pendingRef.current = pending;
    void runValidation();
  }, [push, runValidation]);

  /**
   * Asks about the next outstanding concern, or stages everything when none are
   * left.
   *
   * Locations are grouped by concern so a request spanning twenty descriptions
   * asks at most three questions rather than twenty.
   */
  const askNextConcern = useCallback(() => {
    const concern = concernQueueRef.current[0];
    if (!concern) {
      stageAssessedDescriptions();
      return;
    }

    const affected = assessedRef.current.filter((assessed) => assessed.concern === concern);
    const rationale = affected[0].rationale;
    const names = affected.map((assessed) => assessed.locationName);

    setPhase('resolving');
    push(
      'assistant',
      affected.length === 1
        ? `The description on **${names[0]}** ${rationale}. How should I handle it?`
        : `${pluralize(affected.length, 'location')} ${rationale} — ${listNames(names)}. ` +
          'How should I handle those?',
      { kind: 'choices', options: optionsForConcern(concern) },
    );
  }, [push, stageAssessedDescriptions]);

  /** Assesses the instruction against every target, asking first where it does not fit. */
  const stageDescription = useCallback(
    (text: string) => {
      const source = exportRef.current;
      const targets = targetsRef.current;
      if (!source || targets.length === 0) return;

      appendTextRef.current = text;
      assessedRef.current = targets.map((target) =>
        assessRow({
          locationId: target.id,
          locationName: target.name,
          currentDescription: cellAt(rowFor(source, target.id), source.columns.description),
          text,
        }),
      );

      const present = new Set(
        assessedRef.current
          .map((assessed) => assessed.concern)
          .filter((concern): concern is ConcernType => concern !== null),
      );
      concernQueueRef.current = CONCERN_ORDER.filter((concern) => present.has(concern));

      askNextConcern();
    },
    [askNextConcern],
  );

  /** Runs an intent against the already-resolved target locations. */
  const executeIntent = useCallback(
    async (intent: AssistantIntent) => {
      const source = exportRef.current;
      const targets = targetsRef.current;
      if (!source || targets.length === 0 || intent.action === 'UNKNOWN') return;

      const scopeText =
        targets.length === 1
          ? `**${targets[0].name}**`
          : `**${describeScope(scopeRef.current, targets.length)}**`;

      if (intent.action === 'DRAFT_DESCRIPTION') {
        setPhase('drafting');
        push(
          'assistant',
          `Here is what I would add to the description on ${scopeText}. ` +
            'Edit it however you like, then use it.',
          { kind: 'draft', text: intent.draft },
        );
        return;
      }

      if (intent.action === 'SET_FIELD') {
        pendingRef.current = new Map(
          targets.map((target) => [target.id, { [intent.column]: intent.value }]),
        );
        await runValidation();
        return;
      }

      if (intent.action === 'SET_FIELDS') {
        const changes = Object.fromEntries(intent.fields.map((field) => [field.column, field.value]));
        pendingRef.current = new Map(targets.map((target) => [target.id, { ...changes }]));
        await runValidation();
        return;
      }

      stageDescription(intent.text);
    },
    [push, runValidation, stageDescription],
  );

  /**
   * Answers a question about the user's locations.
   *
   * Kept entirely separate from the write path: this only ever produces text,
   * so a misread question costs a wrong sentence rather than a wrong listing.
   * Unrecognised questions say what the assistant *can* answer instead of
   * reciting the list of things it can change, which reads as broken.
   */
  const answerQuestion = useCallback(
    async (message: string, plan: QuestionPlan | null) => {
      // Re-resolved from the id rather than handed over by the planner, so the
      // planner can only ever name a skill this account already had.
      const query = plan ? queryById(plan.queryId, capabilities.audience) : null;

      if (!query) {
        // No hand-written skill covers this. Before declining, hand it to the
        // backend agent, which can answer from live rows the fixed skill set
        // never anticipated. A disabled or unreachable agent returns null, in
        // which case the assistant declines exactly as it always did — the
        // agent widens what can be answered without changing the floor.
        setPhase('answering');
        let reply: Awaited<ReturnType<AssistantAgent['ask']>> = null;
        try {
          reply = await agent.ask(message);
        } catch {
          // createAgent already swallows failures, but a caller could supply an
          // agent that throws; treat that the same as no answer.
          reply = null;
        }

        if (reply && reply.text.trim().length > 0) {
          // Only answers the agent actually derived from an endpoint get the
          // caveat: a model composing over live rows can still be wrong, and
          // this widget's other answers are deterministic, so the difference is
          // worth flagging. A plain decline ("I cannot answer that yet") used no
          // tool and needs no such warning.
          const text =
            reply.toolsUsed.length > 0
              ? `${reply.text}\n\n**Heads up:** this was put together by AI from your data — worth a quick check before you rely on it.`
              : reply.text;
          push('assistant', text);
          setPhase('idle');
          resetWorkingState();
          return;
        }

        const answerable = describeAnswerableQuestions(capabilities.audience);
        push('assistant', answerable ? CANNOT_ANSWER_YET : NO_SKILLS_YET);
        setPhase('idle');
        return;
      }

      setPhase('answering');

      try {
        /*
         * Only suppliers own locations, and the export this scoping is built on
         * is a supplier-only endpoint. A buyer asking "how many bookings do I
         * have" needs no location context at all, so fetching it would turn a
         * perfectly answerable question into "you do not have any locations
         * yet" — or an error, depending on how the backend refuses it. Their
         * questions are answered account-wide instead, which is also the honest
         * scope: a buyer has no locations of their own to break down by.
         *
         * A supplier's lookup of one named record takes the same path: it never
         * reads the location context, and the export is not readable by every
         * supplier-side user.
         */
        if (capabilities.audience !== 'supplier' || query.needsLocations === false) {
          const answer = await query.answer({
            scope: { kind: 'ALL' },
            targets: [],
            wholeAccount: true,
            period: resolvePeriod(message),
            valueFor: () => '',
            message,
            audience: capabilities.audience,
            geocode,
          });
          push(
            'assistant',
            answer.text,
            answerAttachment(answer),
          );
          setPhase('idle');
          resetWorkingState();
          return;
        }

        const source = await loadExport();
        const candidates = candidatesFrom(source);

        if (candidates.length === 0) {
          push('assistant', 'You do not have any locations yet.');
          setPhase('idle');
          resetWorkingState();
          return;
        }

        const { scope, phrase } = detectScope(message);
        const match = resolveLocationFromMessage(stripScopePhrase(message, phrase), candidates);

        let targets = candidates;
        // Naming nothing means the question is about the account as a whole,
        // which several answers can satisfy in one request instead of one per
        // location.
        let wholeAccount = scope.kind === 'ALL' || match.status === 'NONE';

        if (scope.kind === 'STATUS') {
          targets = targetsForScope(scope, candidates);
          wholeAccount = false;
          if (targets.length === 0) {
            push('assistant', `None of your locations are ${scope.word}.`);
            setPhase('idle');
            resetWorkingState();
            return;
          }
        } else if (match.status === 'RESOLVED') {
          targets = [match.location];
          wholeAccount = false;
        } else if (match.status === 'AMBIGUOUS') {
          // Reads are cheap to get slightly wrong, and the breakdown names each
          // location, so covering both beats interrupting to ask.
          targets = match.candidates;
          wholeAccount = false;
        }

        const valueFor = (locationId: string, column: string): string => {
          const index = source.columns.indexByColumn[column];
          if (index === undefined) return '';
          const row = source.rows.find(
            (candidate) => cellAt(candidate, source.columns.locationId) === locationId,
          );
          return row ? cellAt(row, index) : '';
        };

        const answer = await query.answer({
          scope,
          targets,
          wholeAccount,
          period: resolvePeriod(message),
          valueFor,
          message,
          audience: capabilities.audience,
          geocode,
        });
        push(
          'assistant',
          answer.text,
          answerAttachment(answer),
        );
      } catch (err) {
        const message2 =
          err instanceof BulkEditFormatError
            ? err.message
            : getErrorMessage(err) || 'I could not look that up just now.';
        push('assistant', message2);
      } finally {
        setPhase('idle');
        resetWorkingState();
      }
    },
    [agent, capabilities, geocode, loadExport, push, targetsForScope],
  );

  /** Entry point: parse the instruction, work out the targets, then stage it. */
  const sendMessage = useCallback(
    async (input: string) => {
      const trimmed = input.trim();
      if (!trimmed || phase !== 'idle') return;

      push('user', trimmed);

      const intent = parseIntent(trimmed);
      if (intent.action === 'UNKNOWN') {
        // "What can you do" is answered before anything else looks at the
        // message. It is a question about the assistant rather than about the
        // account's data, so routing it through the skill registry would only
        // give it a chance to be misread as one.
        if (isHelpRequest(trimmed)) {
          push(
            'assistant',
            buildCapabilityReply({
              audience: capabilities.audience,
              canEditLocations: capabilities.canEditLocations,
              editExamples: examples.editExamples,
            }),
          );
          return;
        }

        // A plainly navigational request ("how do I update my payment method")
        // is answered here, before the planner or the backend agent, with an
        // offer to open the page. Nothing moves until the user confirms, so this
        // only ever saves them a click — and it keeps this whole class of
        // question off the model.
        //
        // A message naming one specific record ("find booking 1021445") skips
        // the offer: its lookup answers it outright and links to the same page,
        // filtered to that record.
        const destination = claimantFor(trimmed, capabilities.audience)
          ? null
          : matchNavigation(trimmed, capabilities.audience);
        if (destination) {
          push('assistant', destination.text, {
            kind: 'navigate',
            path: destination.path,
            label: destination.label,
          });
          return;
        }

        // Questions are answered rather than refused — but only after the
        // instruction parser has had its say, since "can you update the
        // capacity on Cheyenne to 1433" is phrased as a question and is
        // plainly a write.
        //
        // A recognised question wins on content even when it does not read as
        // one grammatically, which covers a typo in the opening word. Falling
        // back to the grammar check keeps the "here is what I can answer" reply
        // available for questions that are not in the registry at all.
        // Planned once here and handed down, so routing and answering agree on
        // the same selection and the planner is not asked twice. Routing on the
        // planner's own verdict is the point of the seam: a model planner can
        // recognise a question the phrase matcher would have refused, and that
        // is worth nothing if a matcher-based gate rejects it first.
        const plan = await planner.plan(
          trimmed,
          toolManifest(capabilities.audience),
          capabilities.audience,
        );
        if (plan !== null || isQuestion(trimmed)) {
          await answerQuestion(trimmed, plan);
          return;
        }
        // The parser has no account context, so its generic help names no
        // location — and promises edits an account without write access cannot
        // make. Swap in copy that fits both.
        if (intent.genericHelp) {
          const answerable = describeAnswerableQuestions(capabilities.audience);
          push(
            'assistant',
            capabilities.canEditLocations
              ? examples.unknownHelp
              : answerable
                ? CANNOT_ANSWER_YET
                : NO_SKILLS_YET,
          );
        } else {
          push('assistant', intent.reason);
        }
        return;
      }

      // Every remaining intent is a write. Refuse before touching the bulk-edit
      // pipeline rather than letting the backend reject it after the user has
      // composed and confirmed a change.
      if (!capabilities.canEditLocations) {
        const answerable = describeAnswerableQuestions(capabilities.audience);
        push(
          'assistant',
          answerable
            ? `${CANNOT_EDIT} I can answer questions though \u2014 ask me "what can you do".`
            : CANNOT_EDIT,
        );
        return;
      }

      setPhase('thinking');

      try {
        const source = await loadExport();
        const candidates = candidatesFrom(source);

        if (candidates.length === 0) {
          push('assistant', 'You do not have any locations I can edit yet.');
          setPhase('idle');
          resetWorkingState();
          return;
        }

        const { scope, phrase } = detectScope(trimmed);
        // Name matching runs on what is left after the scope phrase, so a
        // location called "All" cannot narrow "all my locations" to itself.
        const match = resolveLocationFromMessage(stripScopePhrase(trimmed, phrase), candidates);

        if (scope.kind !== 'NAMED') {
          if (match.status === 'RESOLVED') {
            push(
              'assistant',
              `That reads as both every matching location and **${match.location.name}** ` +
                'specifically, so I have not changed anything. Ask me again with just one or the other.',
            );
            setPhase('idle');
            resetWorkingState();
            return;
          }

          if (scope.kind === 'STATUS' && (locations ?? []).length === 0) {
            push(
              'assistant',
              `I cannot tell which of your locations are ${scope.word} right now, ` +
                'so I have not changed anything. Name the locations instead.',
            );
            setPhase('idle');
            resetWorkingState();
            return;
          }

          const targets = targetsForScope(scope, candidates);
          if (targets.length === 0) {
            push(
              'assistant',
              scope.kind === 'STATUS'
                ? `None of your locations are ${scope.word}, so there is nothing to change.`
                : 'You do not have any locations I can edit yet.',
            );
            setPhase('idle');
            resetWorkingState();
            return;
          }

          scopeRef.current = scope;
          targetsRef.current = targets;
          push('assistant', `That covers ${describeScope(scope, targets.length)}. Checking them now.`);
          await executeIntent(intent);
          return;
        }

        if (match.status === 'RESOLVED') {
          scopeRef.current = scope;
          targetsRef.current = [match.location];
          await executeIntent(intent);
          return;
        }

        // Not confident which location was meant — ask rather than guess, since
        // picking wrong would edit a listing the user never mentioned.
        pendingIntentRef.current = intent;
        setPhase('targeting');
        push(
          'assistant',
          match.status === 'AMBIGUOUS'
            ? 'More than one of your locations goes by that name. Which one did you mean?'
            : 'Which location should I update?',
          {
            kind: 'locations',
            candidates: match.status === 'AMBIGUOUS' ? match.candidates : candidates,
          },
        );
      } catch (err) {
        const message =
          err instanceof BulkEditFormatError
            ? err.message
            : getErrorMessage(err) || 'I could not read your locations. Nothing was changed.';
        push('assistant', message);
        setPhase('idle');
        resetWorkingState();
      }
    },
    [
      executeIntent,
      answerQuestion,
      capabilities.audience,
      capabilities.canEditLocations,
      planner,
      examples.unknownHelp,
      examples.editExamples,
      loadExport,
      locations,
      phase,
      push,
      targetsForScope,
    ],
  );

  /** Records the location the user picked and resumes the pending instruction. */
  const chooseLocation = useCallback(
    async (locationId: string) => {
      const source = exportRef.current;
      const intent = pendingIntentRef.current;
      resolveLastAttachment();

      if (!source || !intent) {
        setPhase('idle');
        return;
      }

      const chosen = candidatesFrom(source).find((candidate) => candidate.id === locationId);
      if (!chosen) {
        push('assistant', 'I could not find that location. Nothing was changed.');
        setPhase('idle');
        resetWorkingState();
        return;
      }

      targetsRef.current = [chosen];
      scopeRef.current = { kind: 'NAMED' };
      pendingIntentRef.current = null;
      push('user', chosen.name);
      setPhase('thinking');

      try {
        await executeIntent(intent);
      } catch (err) {
        const message =
          err instanceof BulkEditFormatError
            ? err.message
            : getErrorMessage(err) || 'I could not read that location. Nothing was changed.';
        push('assistant', message);
        setPhase('idle');
        resetWorkingState();
      }
    },
    [executeIntent, push, resolveLastAttachment],
  );

  /** Accepts a (possibly user-edited) draft sentence and stages it. */
  const acceptDraft = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const source = exportRef.current;
      if (!trimmed || !source || targetsRef.current.length === 0) return;

      resolveLastAttachment();
      setPhase('thinking');

      try {
        stageDescription(trimmed);
      } catch (err) {
        const message =
          err instanceof BulkEditFormatError
            ? err.message
            : getErrorMessage(err) || 'I could not read that location. Nothing was changed.';
        push('assistant', message);
        setPhase('idle');
        resetWorkingState();
      }
    },
    [push, resolveLastAttachment, stageDescription],
  );

  /** Records how the flagged descriptions sharing the current concern should merge. */
  const chooseStrategy = useCallback(
    (strategy: ResolutionStrategy) => {
      const concern = concernQueueRef.current[0];
      resolveLastAttachment();

      if (!concern) return;

      const text = appendTextRef.current;
      assessedRef.current = assessedRef.current.map((assessed) => {
        if (assessed.concern !== concern) return assessed;
        return {
          ...assessed,
          strategy,
          proposedDescription: applyStrategy(
            assessed.currentDescription,
            text,
            strategy,
            detectBulletMarker(assessed.currentDescription),
          ),
        };
      });

      concernQueueRef.current = concernQueueRef.current.slice(1);
      askNextConcern();
    },
    [askNextConcern, resolveLastAttachment],
  );

  /** Applies the previously validated file. The server re-validates before writing. */
  const confirmApply = useCallback(async () => {
    const file = fileRef.current;
    if (!file) return;

    resolveLastAttachment();
    setPhase('applying');

    try {
      const response = await applyBulkLocationEdit(file);
      const result = response.body as BulkLocationEditApplyResult;

      if (result.applied) {
        push('assistant', `Done — saved ${pluralize(result.preview.fieldsChanged, 'change')}.`);
        onApplied?.();
      } else {
        push('assistant', 'The server re-checked that change and did not save it:', {
          kind: 'preview',
          preview: result.preview,
        });
      }
    } catch (err) {
      push('assistant', getErrorMessage(err) || 'I could not save that change. Nothing was saved.');
    } finally {
      setPhase('idle');
      resetWorkingState();
    }
  }, [onApplied, push, resolveLastAttachment]);

  const cancelPending = useCallback(() => {
    resolveLastAttachment();
    push('assistant', 'Cancelled — nothing was saved.');
    setPhase('idle');
    resetWorkingState();
  }, [push, resolveLastAttachment]);

  /**
   * Acts on a confirmed navigation offer: hides the buttons and moves the page.
   * The panel closes itself afterwards so the destination is actually visible.
   */
  const confirmNavigation = useCallback(
    (path: string) => {
      resolveLastAttachment();
      onNavigate?.(path);
    },
    [onNavigate, resolveLastAttachment],
  );

  /** Declines a navigation offer, leaving the page where it is. */
  const dismissNavigation = useCallback(() => {
    resolveLastAttachment();
  }, [resolveLastAttachment]);

  /** Discards the whole conversation, including anything staged but unsaved. */
  const clearConversation = useCallback(() => {
    resetWorkingState();
    clearStoredConversation(userId);
    idRef.current = 0;
    setPhase('idle');
    setInput('');
    setMessages([greeting()]);
  }, [userId]);

  return {
    messages,
    phase,
    input,
    examples,
    setInput,
    sendMessage,
    chooseLocation,
    acceptDraft,
    chooseStrategy,
    confirmApply,
    cancelPending,
    confirmNavigation,
    dismissNavigation,
    clearConversation,
  };
};
